import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useI18n } from "../i18n/I18nProvider";
import { brand } from "../theme";
import {
  fetchEmployeeIdByAuthUserId,
  fetchEvents,
  getCurrentUser,
  upsertEmployeeAssessmentOccasion,
} from "../api/queries";
import {
  getSelectedAssessmentOccasion,
  saveSelectedAssessmentOccasion,
} from "./utils/assessmentOccasion";
import type { AssessmentOccasion } from "./utils/assessmentOccasion";
import type { EventRow } from "../types/events";
import {
  AssessmentHeader,
  AssessmentSectionIntro,
  assessmentContentSx,
  assessmentOuterSx,
  assessmentOutlineButtonSx,
  assessmentPageSx,
  assessmentPanelSx,
  assessmentPrimaryButtonSx,
  getAnswerOptionSx,
} from "./AssessmentTestLayout";

export default function OccasionSelection(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const i18n: any = useI18n();
  const lang: "en" | "de" = i18n.lang ?? "en";
  const setLang: (l: "en" | "de") => void = i18n.setLang ?? (() => {});
  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);
  const [events, setEvents] = React.useState<EventRow[]>([]);
  const [eventsLoading, setEventsLoading] = React.useState(true);
  const [occasion, setOccasion] = React.useState<AssessmentOccasion | null>(() =>
    getSelectedAssessmentOccasion()
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const UI = React.useMemo(
    () => ({
      title: L("Choose your occasion", "Anlass auswählen"),
      subtitle: L(
        "Select the context for the readiness questions.",
        "Wähle den Kontext für die Readiness-Fragen."
      ),
      back: L("Previous", "Zurück"),
      next: L("Continue", "Weiter"),
      saving: L("Saving…", "Speichert…"),
      errNotAuth: L("Not authenticated. Please log in again.", "Nicht angemeldet. Bitte erneut einloggen."),
      errSave: L("Could not save the selected occasion.", "Der ausgewählte Anlass konnte nicht gespeichert werden."),
      errLoadEvents: L("Could not load events.", "Anlässe konnten nicht geladen werden."),
      noEvents: L("No events are available yet.", "Es sind noch keine Anlässe verfügbar."),
    }),
    [L]
  );

  React.useEffect(() => {
    let mounted = true;

    async function loadEvents() {
      setEventsLoading(true);

      const { data, error: fetchErr } = await fetchEvents();

      if (!mounted) return;

      if (fetchErr) {
        setError(UI.errLoadEvents);
        setEventsLoading(false);
        return;
      }

      const rows = (data ?? []) as EventRow[];
      setEvents(rows);

      setOccasion((current) => {
        if (current && rows.some((row) => row.id === current)) return current;
        return rows[0]?.id ?? null;
      });

      setEventsLoading(false);
    }

    loadEvents();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseOccasion = (_event: React.MouseEvent<HTMLElement>, next: AssessmentOccasion | null) => {
    if (!next) return;
    setOccasion(next);
    saveSelectedAssessmentOccasion(next);
  };

  const continueToProfile = async () => {
    if (!occasion) return;

    setSaving(true);
    setError(null);

    try {
      const { data: userData, error: userErr } = await getCurrentUser();
      if (userErr || !userData?.user) throw new Error(UI.errNotAuth);

      const { data: employee, error: employeeErr } =
        await fetchEmployeeIdByAuthUserId(userData.user.id);

      if (employeeErr || !employee?.id) throw new Error(UI.errSave);

      const { error: saveErr } = await upsertEmployeeAssessmentOccasion(employee.id, occasion);
      if (saveErr) {
        console.warn("Could not persist assessment occasion; continuing with local selection.", saveErr);
      }

      saveSelectedAssessmentOccasion(occasion);
      handlePageChange("ProfileAssessment");
    } catch (e: any) {
      setError(e?.message ?? UI.errSave);
    } finally {
      setSaving(false);
    }
  };

  function formatEventLabel(event: EventRow) {
    if (!event.event_date) return event.name;
    const date = new Date(`${event.event_date}T00:00:00`);
    const formatted = date.toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    return `${event.name} - ${formatted}`;
  }

  return (
    <Box sx={assessmentPageSx}>
      <Box sx={assessmentOuterSx}>
        <AssessmentHeader
          icon={<FreeBreakfastOutlinedIcon />}
          title={UI.title}
          subtitle={UI.subtitle}
          actions={
            <ToggleButtonGroup
              size="small"
              exclusive
              value={lang}
              onChange={(_, next) => {
                if (next) setLang(next);
              }}
              sx={{
                bgcolor: brand.surfaceMuted,
                border: `1px solid ${brand.border}`,
                borderRadius: 999,
                overflow: "hidden",
                "& .MuiToggleButton-root": {
                  px: 1.1,
                  py: 0.45,
                  fontWeight: 900,
                  textTransform: "none",
                  border: 0,
                  color: brand.textSecondary,
                },
                "& .Mui-selected": {
                  color: `${brand.onInkPrimary} !important`,
                  bgcolor: `${brand.ink} !important`,
                },
              }}
            >
              <ToggleButton value="en">EN</ToggleButton>
              <ToggleButton value="de">DE</ToggleButton>
            </ToggleButtonGroup>
          }
        />

        <Paper elevation={0} sx={assessmentPanelSx}>
          <Box sx={assessmentContentSx}>
            <Stack spacing={2}>
              <AssessmentSectionIntro
                eyebrow={L("Before you start", "Bevor es losgeht")}
                title={UI.title}
                subtitle={UI.subtitle}
              />

              {error ? (
                <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                  {error}
                </Alert>
              ) : null}

              {eventsLoading ? (
                <Stack spacing={1.25}>
                  <Skeleton variant="rounded" height={118} />
                  <Skeleton variant="rounded" height={118} />
                </Stack>
              ) : events.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                  {UI.noEvents}
                </Alert>
              ) : (
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  value={occasion}
                  onChange={chooseOccasion}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.25,
                    "& .MuiToggleButtonGroup-grouped": {
                      m: 0,
                      borderRadius: "12px !important",
                    },
                  }}
                >
                  {events.map((event, index) => (
                    <ToggleButton key={event.id} value={event.id} sx={choiceSx(occasion === event.id)}>
                      <Choice
                        icon={index === 0 ? <FreeBreakfastOutlinedIcon /> : <EventOutlinedIcon />}
                        title={formatEventLabel(event)}
                      />
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                justifyContent="space-between"
                sx={{ pt: 0.5 }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => handlePageChange("ReadinessCheck")}
                  disabled={saving}
                  sx={assessmentOutlineButtonSx}
                >
                  {UI.back}
                </Button>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={continueToProfile}
                  disabled={saving || !occasion}
                  sx={assessmentPrimaryButtonSx}
                >
                  {saving ? UI.saving : UI.next}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function Choice(props: { icon: React.ReactNode; title: string }) {
  return (
    <Stack spacing={1} alignItems="center" sx={{ width: "100%", textAlign: "center" }}>
      <Box sx={{ color: brand.textPrimary, display: "grid", placeItems: "center", fontSize: 28 }}>
        {props.icon}
      </Box>
      <Typography sx={{ fontWeight: 900, lineHeight: 1.25, fontSize: 19, color: brand.textPrimary }}>
        {props.title}
      </Typography>
    </Stack>
  );
}

function choiceSx(selected: boolean) {
  return {
    ...getAnswerOptionSx(selected),
    alignItems: "center",
    justifyContent: "center",
    minHeight: 118,
    p: 2,
    textTransform: "none",
  } as const;
}
